// ============================================================
// ticketPrint.ts — Generación e impresión de boletas en formato
// ticket térmico (80 mm). Abre una ventana emergente y dispara
// window.print(). Como fallback ofrece descarga en HTML.
// ============================================================

const MESES = [
  '', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const moneda = (n: number) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(n);

export const NOMBRE_INSTITUCION = 'Institución Educativa';

// ── Datos del ticket ──────────────────────────────────────────

export interface TicketData {
  tipo: 'MATRICULA' | 'PENSION';
  // Institución
  institucionNombre?: string;
  // Estudiante
  estudianteNombres:   string;
  estudianteApellidos: string;
  estudianteDni:       string;
  // Grado
  anio:          number;
  gradoNombre:   string;
  gradoNivel:    string;
  seccionNombre: string;
  // Pago
  montoBase:       number;
  descuento:       number;
  montoFinal:      number;
  montoPagado:     number;
  descuentoNombre?: string | null;
  observacion?:    string | null;
  // Solo pensión
  mes?:           number;
  numeroPension?: number;
  // Meta
  cajeroNombre: string;
  fecha:        string;
}

// ── Bloque HTML de un ticket individual ──────────────────────

function ticketBlock(d: TicketData, etiqueta: 'ORIGINAL' | 'DUPLICADO'): string {
  const institucion = d.institucionNombre ?? NOMBRE_INSTITUCION;
  const tipoLabel   = d.tipo === 'MATRICULA'
    ? `Matrícula ${d.anio}`
    : `Pensión ${d.numeroPension}° – ${MESES[d.mes ?? 0]} ${d.anio}`;

  const row = (label: string, value: string) => `
    <div class="row"><span class="lbl">${label}</span><span>${value}</span></div>`;

  const filaDescuento = d.descuento > 0
    ? row('Descuento:', `- ${moneda(d.descuento)}`)
    : row('Descuento:', moneda(0));

  const filaDescNombre = d.descuentoNombre
    ? `<div class="note">· ${d.descuentoNombre}</div>` : '';

  const filaObs = d.observacion ? row('Obs.:', d.observacion) : '';

  const eslabon = etiqueta === 'DUPLICADO'
    ? `<div class="copia-label">— DUPLICADO —</div>` : '';

  return `
  <div class="ticket">
    ${eslabon}
    <div class="center bold lg" style="word-break:break-word;overflow-wrap:break-word;line-height:1.3">${institucion}</div>
    <div class="center muted sm">Sistema de Administración Escolar — SAE</div>
    <hr class="hr">

    <div class="center" style="margin-bottom:4px">
      <span class="tag">${d.tipo === 'MATRICULA' ? 'MATRÍCULA' : 'PENSIÓN'}</span>
    </div>
    <div class="center bold">${tipoLabel}</div>

    <hr class="hr">

    <div class="bold" style="margin-bottom:3px">ESTUDIANTE</div>
    <div class="bold">${d.estudianteApellidos}, ${d.estudianteNombres}</div>
    ${row('DNI:', d.estudianteDni)}
    ${row('Grado:', `${d.gradoNombre} – Sec. ${d.seccionNombre}`)}
    <div class="muted sm">${d.gradoNivel} · ${d.anio}</div>

    <hr class="hr">

    ${row('Tarifa base:', moneda(d.montoBase))}
    ${filaDescuento}
    ${filaDescNombre}

    <hr class="hr">
    <div class="total">
      <span>TOTAL PAGADO</span>
      <span>${moneda(d.montoPagado)}</span>
    </div>
    <hr class="hr">

    ${row('Fecha pago:', d.fecha)}
    ${row('Atendido por:', d.cajeroNombre)}
    ${filaObs}

    <hr class="hr">
    <div class="center sm">¡Gracias por su pago!</div>
    <div class="center sm muted" style="margin-top:2px">${institucion} © ${new Date().getFullYear()}</div>
  </div>`;
}

// ── HTML completo (1 o 2 copias) ─────────────────────────────

function buildHtml(d: TicketData, duplicado: boolean): string {
  const tipoLabel = d.tipo === 'MATRICULA'
    ? `Matrícula ${d.anio}`
    : `Pensión ${d.numeroPension}° – ${MESES[d.mes ?? 0]} ${d.anio}`;

  // duplicado=true → solo el bloque DUPLICADO; duplicado=false → solo el ORIGINAL
  const contenido = duplicado
    ? ticketBlock(d, 'DUPLICADO')
    : ticketBlock(d, 'ORIGINAL');

  return `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <title>${duplicado ? 'Duplicado' : 'Boleta'} – ${tipoLabel}</title>
  <style>
    @page  { margin: 0; size: 80mm auto; }
    *      { box-sizing: border-box; margin: 0; padding: 0; }
    body   {
      font-family: 'Courier New', Courier, monospace;
      font-size: 12px;
      color: #111;
      line-height: 1.45;
      background: #fff;
    }
    .ticket {
      width: 302px;
      padding: 10px 14px 14px;
    }
    .center      { text-align: center; }
    .bold        { font-weight: 700; }
    .sm          { font-size: 10px; }
    .lg          { font-size: 14px; }
    .muted       { color: #555; }
    .hr          { border: none; border-top: 1px dashed #666; margin: 7px 0; }
    .row         { display: flex; justify-content: space-between; gap: 6px; margin: 2px 0; }
    .lbl         { color: #555; white-space: nowrap; }
    .total       { display: flex; justify-content: space-between; font-weight: 700; font-size: 14px; margin: 4px 0; }
    .tag         {
      display: inline-block;
      background: #111; color: #fff;
      padding: 2px 7px; border-radius: 3px;
      font-size: 10px; letter-spacing: 0.5px;
    }
    .note        { color: #555; font-size: 10px; margin: 1px 0 2px 0; }
    .copia-label {
      text-align: center; font-weight: 700; font-size: 11px;
      letter-spacing: 2px; color: #555; margin-bottom: 6px;
    }
    .separador   { border-top: 2px dashed #999; margin: 0; }

    @media print {
      .no-print  { display: none !important; }
      .separador { page-break-before: always; border: none; }
    }
    @media screen {
      body       { padding: 20px; background: #f0f0f0; }
      .ticket    { margin: 0 auto 0; border: 1px dashed #aaa; border-radius: 8px; background: #fff; }
      .separador { margin: 16px auto; width: 302px; }
      .no-print  {
        display: flex; gap: 8px; justify-content: center;
        padding: 16px 0 4px;
      }
      .btn {
        padding: 8px 18px; border: none; border-radius: 7px;
        cursor: pointer; font-size: 13px; font-weight: 700;
        font-family: Arial, sans-serif;
      }
      .btn-print    { background: #1d4ed8; color: #fff; }
      .btn-download { background: #166534; color: #fff; }
    }
  </style>
</head>
<body>

  ${contenido}

  <div class="no-print">
    <button class="btn btn-print" onclick="window.print()">Imprimir</button>
    <button class="btn btn-download" onclick="descargar()">Descargar</button>
  </div>

  <script>
    function descargar() {
      var html = document.documentElement.outerHTML;
      var blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'boleta_${d.tipo.toLowerCase()}_${d.estudianteDni}.html';
      a.click();
    }
  </script>
</body>
</html>`;
}

// ── API pública ───────────────────────────────────────────────

/**
 * Abre el ticket en ventana emergente y dispara window.print().
 * @param duplicado  Si true imprime original + duplicado en la misma sesión.
 */
export function imprimirTicket(data: TicketData, duplicado = false): Window | null {
  const win = window.open('', '_blank', 'width=400,height=760,scrollbars=yes,resizable=yes');
  if (!win) return null;
  win.document.open();
  win.document.write(buildHtml(data, duplicado));
  win.document.close();
  win.focus();
  setTimeout(() => { try { win.print(); } catch { /* silenciar */ } }, 400);
  return win;
}

/**
 * Descarga el ticket como PDF vectorial con ancho de ticket térmico
 * (80 mm). Usa jsPDF con texto nativo — sin rasterización — para
 * obtener un resultado nítido a cualquier zoom o resolución de impresión.
 */
export async function descargarTicket(data: TicketData, duplicado = false): Promise<void> {
  const { default: jsPDF } = await import('jspdf');

  // ── Constantes de layout ──────────────────────────────────────
  const W  = 80;   // ancho total mm
  const ML = 4;    // margen izquierdo
  const MR = 4;    // margen derecho
  const CW = W - ML - MR;   // ancho de contenido (72 mm)
  const XR = W - MR;        // borde derecho del contenido

  const institucion = data.institucionNombre ?? NOMBRE_INSTITUCION;

  // ── Helpers ───────────────────────────────────────────────────
  // lh = line-height en mm para un tamaño de fuente en pt
  const lh = (pt: number) => pt * 0.3528 * 1.55;

  // ── Fase 0: medir cuántas líneas ocupa el nombre de la institución ──
  // Se necesita un objeto jsPDF temporal porque splitTextToSize depende
  // del tamaño de fuente activo para calcular anchos de carácter.
  const _tmp = new jsPDF({ unit: 'mm', format: [W, 300], orientation: 'portrait' });
  _tmp.setFont('courier', 'bold');
  _tmp.setFontSize(10);
  const instLines: string[] = _tmp.splitTextToSize(institucion, CW);
  const instLineCount = instLines.length;   // 1 para nombres cortos, 2+ para largos

  // ── Primera pasada: calcular altura ──────────────────────────
  let y = 5;
  const advance = (pt: number, extra = 0) => { y += lh(pt) + extra; };

  if (duplicado) advance(7, 1);          // etiqueta DUPLICADO
  advance(10 * instLineCount, 1);        // nombre institución (multi-línea si es largo)
  advance(7,  1);                        // subtítulo SAE
  y += 3;                         // separador
  advance(7,  1);                 // tag tipo
  advance(8,  1);                 // tipo label
  y += 3;                         // separador
  advance(7,  0.5);               // "ESTUDIANTE"
  advance(8,  0.5);               // apellidos, nombres
  advance(7,  0.5);               // DNI
  advance(7,  0.5);               // Grado
  advance(7,  2);                 // nivel · año
  y += 3;                         // separador
  advance(7,  0.5);               // tarifa base
  advance(7,  0.5);               // descuento
  if (data.descuentoNombre) advance(6.5, 0.5);
  y += 3;                         // separador
  advance(10, 1);                 // TOTAL PAGADO
  y += 3;                         // separador
  advance(7,  0.5);               // fecha
  advance(7,  0.5);               // cajero
  if (data.observacion) advance(7, 0.5);
  y += 3;                         // separador
  advance(6.5, 0.5);             // ¡Gracias!
  advance(6.5, 0);               // copyright
  y += 4;                         // margen inferior

  const HEIGHT = Math.ceil(y);

  // ── Crear PDF ─────────────────────────────────────────────────
  const pdf = new jsPDF({ unit: 'mm', format: [W, HEIGHT], orientation: 'portrait' });
  pdf.setFont('courier');

  // ── Segunda pasada: dibujar ───────────────────────────────────
  y = 5;

  const texto = (txt: string, x: number, align: 'left' | 'center' | 'right' = 'left') => {
    pdf.text(txt, x, y, { align, maxWidth: CW });
  };
  const row = (label: string, value: string) => {
    pdf.setTextColor(100);
    texto(label, ML);
    pdf.setTextColor(17);
    texto(value, XR, 'right');
  };
  const dashedLine = () => {
    pdf.setLineDashPattern([0.8, 0.8], 0);
    pdf.setDrawColor(120);
    pdf.setLineWidth(0.1);
    pdf.line(ML, y, XR, y);
    y += 3;
  };

  // Etiqueta DUPLICADO
  if (duplicado) {
    pdf.setFontSize(7); pdf.setFont('courier', 'bold'); pdf.setTextColor(100);
    texto('— DUPLICADO —', W / 2, 'center');
    y += lh(7) + 1;
    pdf.setTextColor(17);
  }

  // Nombre institución (centrado, con salto de línea automático si es largo)
  pdf.setFontSize(10); pdf.setFont('courier', 'bold'); pdf.setTextColor(17);
  pdf.text(instLines, W / 2, y, { align: 'center' });
  y += lh(10) * instLineCount + 1;

  // Subtítulo
  pdf.setFontSize(7); pdf.setFont('courier', 'normal'); pdf.setTextColor(100);
  texto('Sistema de Administración Escolar — SAE', W / 2, 'center');
  y += lh(7) + 1;

  dashedLine();

  // Tag tipo (recuadro negro)
  const tipoTxt  = data.tipo === 'MATRICULA' ? 'MATRÍCULA' : 'PENSIÓN';
  pdf.setFontSize(7); pdf.setFont('courier', 'bold');
  const tagW = pdf.getTextWidth(tipoTxt) + 4;
  const tagX = (W - tagW) / 2;
  const tagH = lh(7) + 1;
  pdf.setFillColor(17, 17, 17);
  pdf.roundedRect(tagX, y - tagH + 0.8, tagW, tagH, 0.8, 0.8, 'F');
  pdf.setTextColor(255);
  texto(tipoTxt, W / 2, 'center');
  y += lh(7) + 1;
  pdf.setTextColor(17);

  // Tipo label
  const tipoLabel = data.tipo === 'MATRICULA'
    ? `Matrícula ${data.anio}`
    : `Pensión ${data.numeroPension}° – ${MESES[data.mes ?? 0]} ${data.anio}`;
  pdf.setFontSize(8); pdf.setFont('courier', 'bold');
  texto(tipoLabel, W / 2, 'center');
  y += lh(8) + 1;

  dashedLine();

  // Estudiante
  pdf.setFontSize(7); pdf.setFont('courier', 'bold'); pdf.setTextColor(17);
  texto('ESTUDIANTE', ML);
  y += lh(7) + 0.5;

  pdf.setFontSize(8); pdf.setFont('courier', 'bold');
  texto(`${data.estudianteApellidos}, ${data.estudianteNombres}`, ML);
  y += lh(8) + 0.5;

  pdf.setFontSize(7); pdf.setFont('courier', 'normal');
  row('DNI:', data.estudianteDni);
  y += lh(7) + 0.5;

  row('Grado:', `${data.gradoNombre} – Sec. ${data.seccionNombre}`);
  y += lh(7) + 0.5;

  pdf.setTextColor(100);
  texto(`${data.gradoNivel} · ${data.anio}`, ML);
  y += lh(7) + 2;
  pdf.setTextColor(17);

  dashedLine();

  // Montos
  pdf.setFontSize(7); pdf.setFont('courier', 'normal');
  row('Tarifa base:', moneda(data.montoBase));
  y += lh(7) + 0.5;

  row('Descuento:', data.descuento > 0 ? `- ${moneda(data.descuento)}` : moneda(0));
  y += lh(7) + 0.5;

  if (data.descuentoNombre) {
    pdf.setFontSize(6.5); pdf.setTextColor(100);
    texto(`· ${data.descuentoNombre}`, ML);
    y += lh(6.5) + 0.5;
    pdf.setTextColor(17);
  }

  dashedLine();

  // Total
  pdf.setFontSize(10); pdf.setFont('courier', 'bold'); pdf.setTextColor(17);
  texto('TOTAL PAGADO', ML);
  texto(moneda(data.montoPagado), XR, 'right');
  y += lh(10) + 1;

  dashedLine();

  // Meta
  pdf.setFontSize(7); pdf.setFont('courier', 'normal');
  row('Fecha pago:', data.fecha);
  y += lh(7) + 0.5;

  row('Atendido por:', data.cajeroNombre);
  y += lh(7) + 0.5;

  if (data.observacion) {
    row('Obs.:', data.observacion);
    y += lh(7) + 0.5;
  }

  dashedLine();

  // Footer
  pdf.setFontSize(6.5); pdf.setFont('courier', 'normal'); pdf.setTextColor(100);
  texto('¡Gracias por su pago!', W / 2, 'center');
  y += lh(6.5) + 0.5;
  texto(`${institucion} © ${new Date().getFullYear()}`, W / 2, 'center');

  // ── Guardar ───────────────────────────────────────────────────
  const nombreArchivo = [
    'boleta',
    data.tipo.toLowerCase(),
    data.estudianteDni,
    duplicado ? 'dup' : '',
    data.fecha.replace(/[/:, ]/g, '-'),
  ].filter(Boolean).join('_');

  pdf.save(`${nombreArchivo}.pdf`);
}
